import { type CheckedState } from '@radix-ui/react-checkbox'
import { useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { IconButton } from 'slab'
import { getGetBrokersUnprocessedEnrollmentsIdQueryKey } from 'utils/web_api'
import { type BrokersEnrollment } from 'utils/web_api.schemas'

import AddParticipantCoverageToEnrollment, {
  BeforeEmployerCoverageStartDateTooltip,
} from './AddParticipantCoverageToEnrollment'
import { UpdateFieldOnChangeFormSelect } from './EditEnrollmentFormFields'
import { type Coverage } from './types'
import {
  getEnrollmentEndDateOptions,
  getEnrollmentStartDateOptionsWithTooltip,
  getFormattedDate,
} from '../../utils'
import BrokersMemberContext from '../context/BrokersMemberContext'
import CoverageAuditReasonContext from '../context/CoverageAuditReasonContext'
import {
  getEnrollmentCoveragesQueryKey,
  useGetEnrollmentCoverages,
} from '../hooks/enrollments/useGetEnrollmentCoverages'
import { getEnrollmentInsurancePremiumsQueryKey } from '../hooks/enrollments/useGetEnrollmentInsurancePremiums'
import { useModifyCoverages } from '../hooks/enrollments/useModifyCoverages'
import { useRemoveCoverageFromEnrollment } from '../hooks/enrollments/useRemoveCoverageFromEnrollment'
import { Banner } from '../reusable/Banner'
import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import LoadingSpinnerCentered from '../reusable/LoadingSpinnerCentered'
import { Checkbox } from '../ui/checkbox'

type EditEnrollmentDataProps = {
  enrollment: BrokersEnrollment
}

// EditEnrollmentCoverages is a component that allows the user to edit the coverages for an enrollment.
function EditEnrollmentCoverages ({ enrollment }: EditEnrollmentDataProps) {
  const queryClient = useQueryClient()
  const { memberId, employerIchraConfig, planYear } =
    useContext(BrokersMemberContext)
  const { reason, openReasonDropdown } = useContext(CoverageAuditReasonContext)

  const {
    data: coverages,
    isLoading: isCoveragesLoading,
    isError: isCoveragesError,
  } = useGetEnrollmentCoverages({ enrollmentId: enrollment.id, memberId })
  const [coveragesError, setCoveragesError] = useState<string | null>(null)

  const [selectedCoverages, setSelectedCoverages] = useState<
    Record<string, boolean>
  >(
    coverages
      ?.map((coverage) => coverage.id)
      .reduce((acc, id) => ({ ...acc, [id]: true }), {}) || {}
  )
  const hasSelectedCoverages = Object.values(selectedCoverages).some(
    (selected) => selected
  )

  useEffect(() => {
    setSelectedCoverages(
      coverages
        ?.map((coverage) => coverage.id)
        .reduce((acc, id) => ({ ...acc, [id]: true }), {}) || {}
    )
  }, [coverages])

  const isSelectAllChecked = Object.values(selectedCoverages).every(
    (selected) => selected
  )
  const selectAllHandler = (checked: CheckedState) => {
    setSelectedCoverages(
      Object.keys(selectedCoverages).reduce(
        (acc, key) => ({ ...acc, [key]: Boolean(checked) }),
        {}
      )
    )
  }

  const modifyCoveragesMutation = useModifyCoverages({
    enrollmentId: enrollment.id,
    memberId,
    coverageIds: Object.entries(selectedCoverages)
      .filter(([, selected]) => selected)
      .map(([id]) => id),
  })

  const onUpdateCoverageDates = (field: keyof Coverage, value: string) => {
    modifyCoveragesMutation.mutate(
      { [field]: value, coverage_audit_reason: reason },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(
            getEnrollmentCoveragesQueryKey({
              enrollmentId: enrollment.id,
              memberId,
            }),
            data
          )
          queryClient.invalidateQueries({
            queryKey: getEnrollmentInsurancePremiumsQueryKey({
              enrollmentId: enrollment.id,
              memberId,
            }),
          })
          queryClient.invalidateQueries({
            queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
              enrollment.id
            ),
          })
          toast.success(`Successfully updated ${field} to ${value}`, {
            position: 'top-center',
          })
          setCoveragesError(null)
        },
      }
    )
  }

  const removeCoverageMutation = useRemoveCoverageFromEnrollment({
    enrollmentId: enrollment.id,
    memberId,
  })

  const handleDeleteCoverage = (coverage: Coverage) => {
    if (!reason) {
      openReasonDropdown?.()
      return
    }
    removeCoverageMutation.mutate(
      { coverage_id: coverage.id, coverage_audit_reason: reason },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(
            getEnrollmentCoveragesQueryKey({
              enrollmentId: enrollment.id,
              memberId,
            }),
            data
          )
          queryClient.invalidateQueries({
            queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
              enrollment.id
            ),
          })
          queryClient.invalidateQueries({
            queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
              enrollment.id
            ),
          })
          toast.success(
            `Successfully removed coverage for ${coverage.full_name} from enrollment`,
            {
              position: 'top-center',
            }
          )
          setCoveragesError(null)
        },
        onError: (error) => {
          setCoveragesError(
            `Error removing coverage for ${
              coverage.full_name
            } from enrollment: ${error.errors.join(', ') ?? error.message}`
          )
        },
      }
    )
  }

  if (!planYear) {
    return <LoadingSpinnerCentered />
  }

  const successContent = (
    <div className='p-4 space-y-2'>
      <div className='text-sm font-light'>
        <div>Edit coverages for existing participants below.</div>
        {employerIchraConfig?.coverage_start_date && (
          <div>
            Employer Coverage Start Date:{' '}
            {getFormattedDate(employerIchraConfig.coverage_start_date, {
              timeZone: 'UTC',
            })}
          </div>
        )}
      </div>
      {isCoveragesLoading && <LoadingSpinnerCentered />}
      {isCoveragesError && (
        <Banner
          type='critical'
          titleText='Error loading enrollment coverages'
          className='my-2'
        >
          Please try loading again.
        </Banner>
      )}
      {coveragesError && (
        <Banner
          type='critical'
          titleText='Error updating enrollment'
          className='my-2'
        >
          {coveragesError}
        </Banner>
      )}

      <div className='flex justify-between items-center'>
        <div className='w-[20%] flex items-center'>
          <Checkbox
            checked={isSelectAllChecked}
            onCheckedChange={selectAllHandler}
          />
        </div>
        <div className='w-[40%] font-semibold'>Participant</div>
        <div className='w-[40%] font-semibold'>Coverage</div>
      </div>
      {coverages?.map((coverage) => (
        <div key={coverage.id} className='flex justify-between items-center'>
          <div className='w-[20%] flex items-center'>
            <Checkbox
              checked={selectedCoverages[coverage.id] ?? false}
              onCheckedChange={(checked) =>
                setSelectedCoverages((prev) => ({
                  ...prev,
                  [coverage.id]: Boolean(checked),
                }))}
            />
          </div>
          <div className='w-[40%]'>
            {coverage.full_name} ({coverage.relationship})
          </div>
          <div className='w-[40%]'>
            {getFormattedDate(coverage.start_date, { timeZone: 'UTC' })} -{' '}
            {getFormattedDate(coverage.end_date, { timeZone: 'UTC' })}
          </div>
          <IconButton
            disabled={false}
            onClick={() => handleDeleteCoverage(coverage)}
            icon={X}
            label='Remove coverage'
            variant='secondary'
            size='large'
            data-testid='remove_coverage'
          />
        </div>
      ))}
      {/* TODO: Make these separate components */}
      {coverages && coverages.length > 0 && (
        <div className='flex justify-between items-center'>
          <div className='w-[48%]'>
            <UpdateFieldOnChangeFormSelect
              field='start_date'
              value=''
              onChange={onUpdateCoverageDates}
              options={getEnrollmentStartDateOptionsWithTooltip(
                planYear,
                employerIchraConfig,
                <BeforeEmployerCoverageStartDateTooltip />
              )}
              placeholder='Start date'
              resetValueOnChange
              isDisabled={!hasSelectedCoverages}
              requireReason
            />
          </div>
          <div className='w-[48%]'>
            <UpdateFieldOnChangeFormSelect
              field='end_date'
              value=''
              onChange={onUpdateCoverageDates}
              options={getEnrollmentEndDateOptions(planYear)}
              placeholder='End date'
              resetValueOnChange
              isDisabled={!hasSelectedCoverages}
              requireReason
            />
          </div>
        </div>
      )}
      {(!coverages || (coverages.length === 0)) && (
        <div className='text-sm font-light'>
          No coverages found for this enrollment. Please add coverages below.
        </div>
      )}

      <AddParticipantCoverageToEnrollment
        enrollmentId={enrollment.id}
        coverages={coverages}
      />
    </div>
  )

  return (
    <CollapsibleDataContainer
      heading='Coverages'
      isLoading={isCoveragesLoading}
      error={isCoveragesError || coveragesError}
      localStorageKey='edit-enrollment-coverages'
      successContent={successContent}
    />
  )
}

export default EditEnrollmentCoverages
